import { Component, ReactNode } from 'react';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import { METRIC_NAME } from 'src/types';
import { ErrorDisplay } from './ErrorDisplay';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Application-level error boundary component that catches JavaScript errors anywhere in the child component tree.
 *
 * This component serves as the top-level error handler for the entire Frida Central Website application.
 * It prevents the entire app from crashing when unhandled errors occur in React components.
 *
 * **Usage:**
 * - Wraps the entire application at the root level
 * - Catches errors during rendering, in lifecycle methods, and in constructors
 * - Does NOT catch errors in event handlers, async code, or server-side rendering
 *
 * **Error Handling Flow:**
 * 1. Child component throws an error
 * 2. getDerivedStateFromError() updates state to show error UI
 * 3. componentDidCatch() logs error details for debugging
 * 4. ErrorDisplay component shows user-friendly error message with reload option
 *
 * **Why This Component:**
 * This is a custom implementation rather than using a third-party library because:
 * - Integrates with our specific logging system
 * - Uses our custom ErrorDisplay component for consistent UI
 * - Provides simple reload functionality specific to our app structure
 * - No existing similar component found in the codebase
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Static lifecycle method that updates state when an error is caught.
   * This triggers a re-render with the error UI instead of the crashed component tree.
   *
   * @param _error - The error that was thrown (unused but required by React)
   * @returns New state object with hasError set to true
   */
  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  /**
   * Lifecycle method called when an error is caught by this boundary.
   * Logs error details for debugging and monitoring purposes.
   *
   * @param error - The error object that was thrown
   * @param errorInfo - React-specific error information including component stack
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ErrorBoundary caught error', { error, errorInfo });
    metricsPublisher.emitCount(METRIC_NAME.ERROR_BOUNDARY_TRIGGERED);
  }

  /**
   * Handles the reload action by navigating to the home page.
   * This provides a simple recovery mechanism for users when errors occur.
   */
  private handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return <ErrorDisplay onNavigateHome={this.handleReload} />;
    }

    return this.props.children;
  }
}
